import type { ResizablePanelProps } from './index.types';
import React from 'react';
import { cn } from '~/lib/utils';
import { useDrag } from './hooks/useDrag';
import { useKeyboardResize } from './hooks/useKeyboardResize';
import { useStoredWidth } from './hooks/useStoredWidth';

/**
 * A fixed-width panel that can be resized by pointer drag or keyboard and
 * remembers its width in `localStorage`.
 *
 * The separator follows the [WAI-ARIA window splitter pattern](https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/):
 * Arrow keys nudge the width, and Home and End jump to the minimum and maximum.
 * Width is clamped between `minWidth` and `maxWidth` on every change.
 *
 * @note
 * Use a unique `key` for the panel to ensure the width is reset when the panel is remounted (e.g. when the user changes so we need to apply the new user's preferences).
 *
 * @example
 * ```tsx
 * <ResizablePanel
 *   storageKey="conversations-panel-width"
 *   defaultWidth={320}
 *   minWidth={240}
 *   maxWidth={480}
 *   aria-label="Resize conversation list"
 * >
 *   <ConversationList />
 * </ResizablePanel>
 * ```
 */
export const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  handleSide = 'right',
  className,
  disabled = false,
  'aria-label': ariaLabel = 'Resize panel',
}) => {
  const bounds = React.useMemo(
    () => ({ default: defaultWidth, min: minWidth, max: maxWidth }),
    [defaultWidth, minWidth, maxWidth]
  );

  const { width, widthRef, setWidth, persistWidth } = useStoredWidth({
    storageKey,
    bounds,
  });

  const { isDragging, handlePointerDown, handlePointerMove, handlePointerUp } =
    useDrag({
      disabled,
      handleSide,
      widthRef,
      setWidth,
      persistWidth,
    });

  const { handleKeyDown } = useKeyboardResize({
    disabled,
    handleSide,
    bounds,
    widthRef,
    persistWidth,
  });

  const isRightHandle = handleSide === 'right';

  return (
    <div
      className={cn(
        'relative shrink-0 border-border',
        isRightHandle ? 'border-r' : 'border-l',
        !disabled &&
          'has-[[role=separator]:hover]:border-primary has-[[role=separator]:focus-visible]:border-primary',
        isDragging && 'border-primary',
        className
      )}
      style={{ width }}
    >
      {children}
      {!disabled && (
        <div
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-valuenow={width}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          aria-label={ariaLabel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown}
          className={cn(
            'absolute inset-y-0 z-10 w-[9px] cursor-col-resize touch-none outline-none',
            isRightHandle ? '-right-[8px]' : '-left-[8px]'
          )}
        />
      )}
    </div>
  );
};
